<template>
  <base-modal ref="signupLinksModal" size="3xl" @hiding="reset">
    <base-card class="tw-overlow-auto tw-relative">
      <h3 class="tw-text-xl tw-font-thin">Signup Links</h3>
      <div class="tw-mt-6">
        <div>Web signup</div>
        <div class="tw-text-xs tw-text-gray-700">Use this link on your public website and marketing materials.</div>
        <div class="tw-flex tw-justify-between tw-border tw-rounded-md tw-bg-gray-50 tw-p-2 tw-mt-3">
          <div class="tw-text-xs tw-my-auto tw-italic tw-flex tw-text-gray-600">{{ baseBusinessUrl + "/member-web-signup" }}</div>
          <div class="tw-flex tw-my-auto">
            <div ref="button-tooltip-group" class="tw-group tw-relative tw-w-max">
              <base-button id="webCopyButton" type="flat" class="tw-whitespace-nowrap" @click="copyText('webCopyButton', baseBusinessUrl + '/member-web-signup')">
                Copy Link
                <template v-slot:icon>
                  <copy-icon />
                </template>
              </base-button>
              <base-tooltip ref="copyTooltip" textValue="Link Copied!" />
            </div>
            <base-button type="flat" class="tw-whitespace-nowrap" @click="showWebQr = !showWebQr">
              QR Code
              <template v-slot:icon>
                <qr-icon />
              </template>
            </base-button>
          </div>
        </div>
        <div class="tw-flex tw-flex-col tw-w-full tw-items-center tw-mt-3" v-if="showWebQr === true">
          <vue-qr id="webQrCode" ref="webQrCode" class="tw-rounded-xl tw-border" :text="qrWebLink" :size="250"></vue-qr>
          <base-button type="primary" class="tw-w-1/3 tw-mt-3" @click="downloadQr('web')">Download</base-button>
        </div>
      </div>
      <div class="tw-mt-12" v-if="!isLocationFiltered">
        <div>Location signup link</div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
          <div class="tw-text-xs tw-text-gray-700">
            Use this link to allow a {{ organization.customerLabelSingular }} to signup for a plan inside a particular store or location.
          </div>
          <base-select label="Location" placeholder="Select a location" :optgroups="locationOptGroups" label-key="name" value-key="businessLocationId" v-model="signupLocation" />
        </div>
        <div v-if="signupLocation" class="tw-flex tw-justify-between tw-border tw-rounded-md tw-bg-gray-50 tw-p-2 tw-mt-3">
          <div class="tw-text-xs tw-italic tw-flex tw-text-gray-600 tw-my-auto">{{ qrLocationLink }}</div>
          <div class="tw-flex tw-my-auto">
            <div ref="location-button-tooltip-group" class="tw-group tw-relative tw-w-max">
              <base-button id="locationCopyButton" type="flat" class="tw-whitespace-nowrap" @click="copyText('locationCopyButton', qrLocationLink)">
                Copy Link
                <template v-slot:icon>
                  <copy-icon />
                </template>
              </base-button>
              <base-tooltip ref="locationCopyTooltip" textValue="Link Copied!" />
            </div>
            <base-button type="flat" class="tw-my-auto tw-whitespace-nowrap" @click="showLocationQr = !showLocationQr">
              QR Code
              <template v-slot:icon>
                <qr-icon />
              </template>
            </base-button>
          </div>
        </div>
        <div class="tw-flex tw-flex-col tw-w-full tw-items-center tw-mt-3" v-if="showLocationQr === true">
          <vue-qr id="locationQrCode" ref="locationQrCode" class="tw-rounded-xl tw-border" :text="qrLocationLink" :size="250"></vue-qr>
          <base-button type="primary" class="tw-w-1/3 tw-mt-3" @click="downloadQr('location')">Download</base-button>
        </div>
      </div>
    </base-card>
  </base-modal>
</template>

<script>
import { mapGetters } from "vuex";
import html2canvas from "html2canvas";
import { STATE_OPTIONS } from "@/constants/constants";
import { QrIcon, CopyIcon } from "@/assets/svgs";
import VueQr from "vue-qr";

export default {
  name: "SignupLinks",
  props: {
    businessId: String,
    operatingModel: { type: String, default: "direct" },
  },
  components: {
    QrIcon,
    CopyIcon,
    VueQr,
  },
  data() {
    return {
      signupLocation: null,
      isLoading: false,
      showWebQr: false,
      showLocationQr: false,
      STATE_OPTIONS,
    };
  },
  computed: {
    ...mapGetters("location", ["location", "locations"]),
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("role", ["roles", "isPredictile", "isBusinessAdministrator", "isBusinessManagement", "isBusinessOperations", "isLocationFiltered"]),
    baseBusinessUrl() {
      return location.protocol + "//" + location.host + "/businesses/" + this.businessId;
    },
    qrWebLink() {
      return this.baseBusinessUrl + "/member-web-signup";
    },
    qrLocationLink() {
      return this.signupLocation ? this.baseBusinessUrl + "/locations/" + this.signupLocation + "/member-location-web-signup" : "";
    },
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: this.locations.filter((l) => l.state === s.value),
      }));
    },
  },
  watch: {
    location() {
      this.checkLocation();
    },
    mounted() {
      this.checkLocation();
    },
  },
  methods: {
    show() {
      this.$refs.signupLinksModal.show();
    },
    hide() {
      this.$refs.signupLinksModal.hide();
    },
    reset() {
      this.showWebQr = false;
      this.showLocationQr = false;
      this.signupLocation = null;
    },
    checkLocation() {
      if (this.location) {
        this.signupLocation = this.location;
      }
    },
    async copyText(id, link) {
      await this.$copyText(link);

      if (id === "webCopyButton") {
        this.$refs.copyTooltip.show();
        setTimeout(() => {
          this.$refs.copyTooltip.hide();
        }, 1000);
      } else {
        this.$refs.locationCopyTooltip.show();
        setTimeout(() => {
          this.$refs.locationCopyTooltip.hide();
        }, 1000);
      }
    },
    async downloadQr(type) {
      const el = type === "web" ? this.$refs.webQrCode.$el : this.$refs.locationQrCode.$el;
      const options = {
        type: "dataURL",
      };
      const printCanvas = await html2canvas(el, options);

      const link = document.createElement("a");

      const name =
        type === "web"
          ? ""
          : this.locations
              .find((l) => l.businessLocationId === this.signupLocation)
              .name.replace(/[^A-Z0-9]/gi, "_")
              .toLowerCase();

      link.setAttribute("download", type === "location" ? "signup_qr_" + name + ".png" : "signup_qr.png");
      link.setAttribute("href", printCanvas.toDataURL("image/png").replace("image/png", "image/octet-stream"));
      link.click();
    },
  },
};
</script>
